import { Factory, ShoppingBag } from 'lucide-react'
import { Link } from 'react-router'

import { AUTH_INPUT } from '@/components/auth-layout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SocialAuth, TurnstileWidget } from '@/domains/auth'

import { PasswordInput } from './password-input'
import { useSignupForm } from './use-signup-form'

export function SignupForm() {
  const { state, dispatch, isValid, handleSubmit, googleUser } = useSignupForm()

  /** check the user's linked providers (from better auth's `account` table,
   * surfaced via /v1/me). 'google' = signed in via OAuth, absence = the
   * user reached this surface via the email-verify flow. authoritative,
   * not heuristic. unlike checking user.image, which would mislabel a
   * future oauth provider that omits the avatar field.
   */
  const authedViaGoogle = googleUser?.providers.includes('google') ?? false

  return (
    <div className='mx-auto w-full max-w-[420px]'>
      <h1 className='font-display text-[36px] leading-[1.1] tracking-[-0.5px] text-ink'>
        {googleUser ? 'Finish signing up' : 'Get started with TandemOne'}
      </h1>
      <p className='mt-2 text-[15px] text-ink-soft'>
        {googleUser
          ? 'One more thing, name your company.'
          : 'A few quick details to get started.'}
      </p>

      {!googleUser && (
        <div className='mt-8'>
          <SocialAuth />
        </div>
      )}
      {authedViaGoogle && googleUser && (
        <div className='mt-8'>
          <SocialAuth signedInEmail={googleUser.email} />
        </div>
      )}

      <form onSubmit={handleSubmit} className='mt-4 flex flex-col gap-4'>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='first-name'>First name</Label>
            <Input
              id='first-name'
              type='text'
              autoComplete='given-name'
              placeholder='Michael'
              value={state.firstName}
              onChange={event =>
                dispatch({
                  type: 'SET_FIRST_NAME',
                  payload: event.target.value
                })
              }
              disabled={!!googleUser}
              className={AUTH_INPUT}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='last-name'>Last name</Label>
            <Input
              id='last-name'
              type='text'
              autoComplete='family-name'
              placeholder='Scott'
              value={state.lastName}
              onChange={event =>
                dispatch({ type: 'SET_LAST_NAME', payload: event.target.value })
              }
              disabled={!!googleUser}
              className={AUTH_INPUT}
            />
          </div>
        </div>
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='email'>Email</Label>
          <Input
            id='email'
            type='email'
            autoComplete='email'
            placeholder='you@company.com'
            value={state.email}
            onChange={event =>
              dispatch({ type: 'SET_EMAIL', payload: event.target.value })
            }
            disabled={!!googleUser}
            className={AUTH_INPUT}
          />
        </div>

        {googleUser ? (
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='company-name'>Company name</Label>
            <Input
              id='company-name'
              type='text'
              autoComplete='organization'
              placeholder='Dunder Mifflin'
              value={state.companyName}
              onChange={event =>
                dispatch({
                  type: 'SET_COMPANY_NAME',
                  payload: event.target.value
                })
              }
              className={AUTH_INPUT}
            />
            <div className='mt-2 flex flex-col gap-1.5'>
              <Label id='company-type-label'>Company type</Label>
              <div
                role='radiogroup'
                aria-labelledby='company-type-label'
                className='flex flex-col gap-2'
              >
                <CompanyTypeOption
                  label='Supplier'
                  description='Get discovered by buyers'
                  icon={Factory}
                  selected={state.companyType === 'supplier'}
                  onSelect={() =>
                    dispatch({ type: 'SET_COMPANY_TYPE', payload: 'supplier' })
                  }
                />
                <CompanyTypeOption
                  label='Buyer'
                  description='Find new suppliers'
                  icon={ShoppingBag}
                  selected={state.companyType === 'buyer'}
                  onSelect={() =>
                    dispatch({ type: 'SET_COMPANY_TYPE', payload: 'buyer' })
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='password'>Password</Label>
              <PasswordInput
                id='password'
                autoComplete='new-password'
                placeholder='At least 8 characters'
                value={state.password}
                onChange={value =>
                  dispatch({ type: 'SET_PASSWORD', payload: value })
                }
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='confirm-password'>Confirm password</Label>
              <PasswordInput
                id='confirm-password'
                autoComplete='new-password'
                value={state.confirmPassword}
                onChange={value =>
                  dispatch({ type: 'SET_CONFIRM_PASSWORD', payload: value })
                }
              />
            </div>
          </>
        )}

        {!googleUser && <TurnstileWidget />}
        {state.error && <p className='text-sm text-red-600'>{state.error}</p>}
        <button
          type='submit'
          disabled={!isValid || state.submitting}
          className='mt-2 w-full rounded-xl bg-accent py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent'
        >
          {state.submitting
            ? googleUser
              ? 'Finishing signup...'
              : 'Signing up...'
            : googleUser
              ? 'Finish signup'
              : 'Signup'}
        </button>
      </form>

      {!googleUser && (
        <>
          <p className='mt-4 text-center text-xs leading-relaxed text-ink-muted'>
            By creating an account, you agree to our{' '}
            <Link
              to='/terms'
              className='text-accent dark:text-accent-mid underline underline-offset-2'
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to='/privacy'
              className='text-accent dark:text-accent-mid underline underline-offset-2'
            >
              Privacy Policy
            </Link>
            .
          </p>
          <p className='mt-8 text-center text-sm text-ink-soft'>
            Already have an account?{' '}
            <Link
              to='/login'
              className='font-medium text-accent dark:text-accent-mid'
            >
              Login
            </Link>
          </p>
        </>
      )}
    </div>
  )
}

function CompanyTypeOption({
  label,
  description,
  icon: Icon,
  selected,
  onSelect
}: {
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      onClick={onSelect}
      className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors ${
        selected
          ? 'border-accent bg-accent-light'
          : 'border-border bg-page hover:border-accent/40'
      }`}
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
          selected
            ? 'bg-accent text-white'
            : 'bg-accent-light text-accent dark:text-accent-mid'
        }`}
      >
        <Icon className='size-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-[14px] font-medium text-ink'>{label}</p>
        <p className='mt-0.5 text-[12px] text-ink-soft'>{description}</p>
      </div>
      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-accent bg-accent' : 'border-border bg-page'
        }`}
      >
        {selected && <span className='size-1.5 rounded-full bg-white' />}
      </span>
    </button>
  )
}
